"use client";
import React, { useEffect, useRef } from "react";
import { useCardSearch } from "modules/cards/stores/useCardsStore";
import { useCardAutocomplete } from "modules/decks/hooks/useCardAutocomplete";
import {
  AutocompleteGameCard,
  FinishEnum,
  NONFOIL_FINISH,
} from "modules/cards/model/card";
import AutocompleteFinish from "./AutocompleteFinish";
import AutocompleteQuantity from "./AutocompleteQuantity";
import {
  TailwindCardAutocomplete,
  TailwindCardAutocompleteHandle,
} from "modules/common/components/autocomplete/TailwindCardAutocomplete";
import {
  useSwapCalculatorStore,
  useSwapFormState,
} from "modules/swap-calculator/stores/useSwapCalculatorStore";

function SwapCalculatorSearchAutocomplete() {
  // Zustand store for card search
  const {
    searchRequest: req,
    setSearchRequest: setReq,
    resetSearchRequest: resetReq,
  } = useCardSearch();
  const { query, loadMoreRef, scrollContainerRef, opts } =
    useCardAutocomplete(req);
  const autocompleteRef = useRef<TailwindCardAutocompleteHandle>(null);
  // Optimal Zustand selector
  const { setSelectedCard, setFinish, setQuantity } = useSwapFormState();

  // Get stable references directly from the store
  const registerAutocompleteReset = useSwapCalculatorStore(
    (state) => state.registerAutocompleteReset
  );
  const unregisterAutocompleteReset = useSwapCalculatorStore(
    (state) => state.unregisterAutocompleteReset
  );

  // Register the autocomplete reset callback with the store
  // Use refs to avoid dependency cycles that cause infinite loops
  const resetReqRef = useRef(resetReq);
  const setSelectedCardRef = useRef(setSelectedCard);
  const setFinishRef = useRef(setFinish);
  const setQuantityRef = useRef(setQuantity);
  const autocompleteRefCurrent = autocompleteRef;

  // Keep refs up to date
  useEffect(() => {
    resetReqRef.current = resetReq;
    setSelectedCardRef.current = setSelectedCard;
    setFinishRef.current = setFinish;
    setQuantityRef.current = setQuantity;
  });

  useEffect(() => {
    // This callback is called from ActionBar when adding a card
    // We call autocompleteRef.reset() which will internally call onClear (handleClear)
    // handleClear will reset the store state, so we don't need to do it here
    const resetCallback = () => {
      autocompleteRefCurrent.current?.reset();
    };

    registerAutocompleteReset(resetCallback);
    return () => {
      unregisterAutocompleteReset();
    };
  }, [
    registerAutocompleteReset,
    unregisterAutocompleteReset,
    autocompleteRefCurrent,
  ]);

  const handleSelect = (card: AutocompleteGameCard) => {
    setSelectedCard(card);
    setFinish(card.finishes[0]);
  };

  const handleInputChange = (value: string) => {
    if (value.length < 3) {
      return;
    }

    setReq({ string: value });
  };

  // Note: Don't call autocompleteRef.current?.reset() here - that would cause infinite loop
  // since the autocomplete's reset() calls onClear which is this function
  const handleClear = () => {
    resetReqRef.current();
    setSelectedCardRef.current(null);
    setFinishRef.current(NONFOIL_FINISH);
    setQuantityRef.current(1);
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-12">
      <div className="order-2 col-span-2 md:order-1 md:col-span-3">
        <AutocompleteQuantity />
      </div>
      <div className="order-1 col-span-12 md:order-2 md:col-span-6">
        <TailwindCardAutocomplete
          ref={autocompleteRef}
          onInputChange={handleInputChange}
          onClear={handleClear}
          options={opts()}
          isLoading={query.isFetching}
          isFetchingNextPage={query.isFetchingNextPage}
          loadMoreRef={loadMoreRef}
          scrollContainerRef={scrollContainerRef}
          onSelect={handleSelect}
          tooltipPlacement="right"
          shouldClearInput={false}
          inputClassName="input-sm md:input-md md:text-base"
        />
      </div>
      <div className="order-3 col-span-10 md:col-span-3">
        <AutocompleteFinish />
      </div>
    </div>
  );
}

export default SwapCalculatorSearchAutocomplete;
